/**
 * What the scanner pipeline accepts. Deliberately loose: a scan has already proved its intent by
 * arriving faster than a person can type, so the shape only has to rule out punctuation and noise.
 */
const isValidSingleBarcode = (barcode: string): boolean => {
  const barcodeRegex = /^[A-Za-z0-9./_-]{4,50}$/
  return barcodeRegex.test(barcode)
}

/**
 * What TYPED text must look like before the palette offers to turn it into a product.
 *
 * The loose rule above cannot be used here. It accepts `TS1042` and `INV-016`, so typing an order
 * number offered "Generate a New Product for TS1042" beside the order it found. Nothing in a
 * document number's shape separates it from a barcode — an order prefix is a tenant setting, an
 * order's short id is six UUID characters, and a bill number is whatever the supplier printed.
 *
 * So this names the one shape that is a barcode and cannot be a document number: the digit counts
 * of EAN-8, UPC-A, EAN-13 and ITF-14.
 */
const isStrictBarcode = (candidate: string): boolean => /^(\d{8}|\d{12}|\d{13}|\d{14})$/.test(candidate)

const parseAndValidateBarcodes = (query?: string): string[] => {
  if (!query || query.trim() === '') return []
  return query
    .split(',')
    .map(bc => bc.trim())
    .filter(bc => bc.length > 0 && isValidSingleBarcode(bc))
}

const formatBarcodesForDisplay = (barcodes: string[], maxDisplay = 2): string => {
  if (barcodes.length === 0) return ''
  return barcodes.length > maxDisplay
    ? `${barcodes.slice(0, maxDisplay).join(', ')}...(+${barcodes.length - maxDisplay})`
    : barcodes.join(', ')
}

/**
 * The barcodes a TYPED query may seed a product with — strict shapes only.
 *
 * @param query - The raw input, possibly several codes separated by commas
 * @returns Every comma-separated part that is a strict barcode. Empty when none is.
 */
const parseTypedBarcodes = (query?: string): string[] => parseAndValidateBarcodes(query).filter(isStrictBarcode)

export { isValidSingleBarcode, isStrictBarcode, parseAndValidateBarcodes, parseTypedBarcodes, formatBarcodesForDisplay }
